import { z } from "zod";

// Email accounts ("senders"): the mailboxes campaigns send from. They are
// connected by the user (Gmail, Outlook, SendGrid, Amazon SES, any SMTP) or
// bought through Managed Mailboxes. Nothing here sends mail: connections, tests and
// warmup are simulated, with the same rules and messages as the reference.

export const providerSchema = z.enum(["gmail", "outlook", "sendgrid", "ses", "smtp"]);
export type Provider = z.infer<typeof providerSchema>;

export const PROVIDERS: ReadonlyArray<{
  readonly id: Provider;
  readonly name: string;
  readonly auth: "oauth" | "api-key" | "password";
}> = [
  { id: "gmail", name: "Gmail", auth: "oauth" },
  { id: "outlook", name: "Outlook", auth: "oauth" },
  { id: "sendgrid", name: "Sendgrid", auth: "api-key" },
  { id: "ses", name: "Amazon SES", auth: "api-key" },
  { id: "smtp", name: "Other", auth: "password" },
];

export const sourceSchema = z.enum(["manual", "managed-mailboxes"]);
export type SenderSource = z.infer<typeof sourceSchema>;

export const senderStatusSchema = z.enum(["active", "provisioning", "invalid"]);
export type SenderStatus = z.infer<typeof senderStatusSchema>;

export const invalidReasonSchema = z.enum(["auth", "permissions", "no-gmail", "generic"]);
export type InvalidReason = z.infer<typeof invalidReasonSchema>;

/** Above this many messages a day, saving asks the user to acknowledge the risk. */
export const PREFERRED_DAILY_LIMIT = 50;
export const MAX_TAGS_PER_SENDER = 10;

export const SMTP_PORTS = [25, 465, 587] as const;
export const IMAP_PORTS = [143, 993] as const;

export const serverSchema = z.object({
  host: z.string().trim().min(1, "Host is required").max(200),
  port: z
    .number()
    .int()
    .min(1, "The port must be between 1 and 65535")
    .max(65_535, "The port must be between 1 and 65535"),
});

export const warmupStatusSchema = z.enum(["off", "processing", "active", "failed", "managed"]);
export type WarmupStatus = z.infer<typeof warmupStatusSchema>;

export const warmupSettingsSchema = z.object({
  enabled: z.boolean(),
  emailsPerDay: z.number().int().min(1, "Between 1-50 emails").max(50, "Between 1-50 emails"),
  replyRate: z.number().int().min(20, "Between 20-100%").max(100, "Between 20-100%"),
  rampup: z.boolean(),
  rampupValue: z.number().int().min(5, "Between 5-20 emails").max(20, "Between 5-20 emails"),
  /** Two code words that mark warmup traffic so filters can catch it. */
  identifier: z.tuple([z.string().trim().max(20), z.string().trim().max(20)]),
});
export type WarmupSettings = z.infer<typeof warmupSettingsSchema>;

export const DEFAULT_WARMUP: WarmupSettings = {
  enabled: false,
  emailsPerDay: 20,
  replyRate: 30,
  rampup: true,
  rampupValue: 5,
  identifier: ["", ""],
};

export const senderSettingsSchema = z.object({
  name: z.string().trim().min(1, "Please fill all fields").max(80),
  messagesPerDay: z
    .number()
    .int()
    .min(1, "Messages per day must be between 1 and 500")
    .max(500, "Messages per day must be between 1 and 500"),
  delaySeconds: z.number().int().min(1, "Seconds between emails must be 1 or more").max(3_600),
  inboxEnabled: z.boolean(),
  signature: z.string().max(5_000),
  tags: z
    .array(z.string().trim().min(1).max(30))
    .max(MAX_TAGS_PER_SENDER, `Maximum of ${MAX_TAGS_PER_SENDER} tags per account`),
});
export type SenderSettings = z.infer<typeof senderSettingsSchema>;

/** What a password-connected account needs; secrets never come back out. */
export const connectionSchema = z.object({
  email: z.email("Enter a valid email address"),
  smtp: serverSchema.extend({
    username: z.string().trim().min(1, "SMTP username is required"),
    password: z.string(),
  }),
  imap: serverSchema.extend({
    /** Null: the reading account shares the sending credentials. */
    username: z.string().trim().nullable(),
    password: z.string().nullable(),
  }),
});
export type ConnectionInput = z.infer<typeof connectionSchema>;

export const senderSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  email: z.string(),
  provider: providerSchema,
  source: sourceSchema,
  status: senderStatusSchema,
  invalidReason: invalidReasonSchema.nullable(),
  /** IMAP failed while sending works: replies cannot be read until reconnected. */
  inboxConnectionFailed: z.boolean(),
  settings: senderSettingsSchema,
  smtp: z.object({ host: z.string(), port: z.number().int(), username: z.string() }).nullable(),
  imap: z.object({ host: z.string(), port: z.number().int(), username: z.string().nullable() }).nullable(),
  warmup: warmupSettingsSchema,
  warmupStatus: warmupStatusSchema,
  /** Emails sent today, against `settings.messagesPerDay`. */
  sentToday: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});
export type Sender = z.infer<typeof senderSchema>;

export const senderListSchema = z.object({
  senders: z.array(senderSchema),
  /** The plan's account limit; null means unlimited. */
  max: z.number().int().nullable(),
  /** Every tag in use, for filters and pickers. */
  tags: z.array(z.string()),
});
export type SenderList = z.infer<typeof senderListSchema>;

export const connectOAuthInputSchema = z.object({
  provider: z.enum(["gmail", "outlook"]),
  email: z.email("Enter a valid email address"),
  name: z.string().trim().min(1).max(80),
});
export type ConnectOAuthInput = z.infer<typeof connectOAuthInputSchema>;

export const connectApiKeyInputSchema = z.object({
  provider: z.enum(["sendgrid", "ses"]),
  email: z.email("Enter a valid email address"),
  name: z.string().trim().min(1).max(80),
  apiKey: z.string().trim().min(10, "Enter the API key"),
});
export type ConnectApiKeyInput = z.infer<typeof connectApiKeyInputSchema>;

export const connectSmtpInputSchema = connectionSchema.extend({ settings: senderSettingsSchema });
export type ConnectSmtpInput = z.infer<typeof connectSmtpInputSchema>;

export const testConnectionInputSchema = connectionSchema.extend({
  /** Re-test a saved account: empty passwords mean "the saved one". */
  senderId: z.string().nullable(),
});
export type TestConnectionInput = z.infer<typeof testConnectionInputSchema>;

export const testOutcomeSchema = z.object({
  sending: z.enum(["ok", "failed"]),
  reading: z.enum(["ok", "failed", "unreachable"]),
});
export type TestOutcome = z.infer<typeof testOutcomeSchema>;

export const updateSenderInputSchema = z.object({
  senderId: z.string(),
  settings: senderSettingsSchema,
  connection: connectionSchema.nullable(),
});
export type UpdateSenderInput = z.infer<typeof updateSenderInputSchema>;

export const updateWarmupInputSchema = z.object({ senderId: z.string(), warmup: warmupSettingsSchema });
export type UpdateWarmupInput = z.infer<typeof updateWarmupInputSchema>;

export const warmupStatsSchema = z.object({
  totals: z.object({ sent: z.number(), inbox: z.number(), received: z.number(), spam: z.number() }),
  daily: z.array(
    z.object({ date: z.string(), sent: z.number(), replies: z.number(), savedFromSpam: z.number() }),
  ),
});
export type WarmupStats = z.infer<typeof warmupStatsSchema>;

/** Bulk edit: every section is optional; only the ones turned on change. */
export const bulkChangesSchema = z.object({
  pacing: z
    .object({ messagesPerDay: z.number().int().min(1).max(500), delaySeconds: z.number().int().min(1) })
    .nullable(),
  projectId: z.string().nullable(),
  addTags: z.array(z.string().trim().min(1).max(30)),
  removeTags: z.array(z.string()),
  smtp: serverSchema.nullable(),
  imap: serverSchema.nullable(),
  signature: z.string().max(5_000).nullable(),
  warmup: z
    .union([z.literal("disable"), warmupSettingsSchema.omit({ enabled: true, identifier: true })])
    .nullable(),
  inbox: z.enum(["enable", "disable"]).nullable(),
});
export type BulkChanges = z.infer<typeof bulkChangesSchema>;

export const bulkErrorSchema = z.enum([
  "SENDER_NOT_FOUND",
  "FIELD_NOT_APPLICABLE",
  "SMTP_TEST_FAILED",
  "IMAP_TEST_FAILED",
  "WARMUP_NOT_SUPPORTED",
  "WARMUP_SENDER_INVALID",
  "TAG_LIMIT",
]);
export type BulkError = z.infer<typeof bulkErrorSchema>;

export const bulkEditInputSchema = z.object({
  senderIds: z.array(z.string()).min(1),
  changes: bulkChangesSchema,
});
export type BulkEditInput = z.infer<typeof bulkEditInputSchema>;

export const bulkEditResultSchema = z.object({
  updated: z.number().int(),
  failed: z.array(z.object({ senderId: z.string(), email: z.string(), error: bulkErrorSchema })),
});
export type BulkEditResult = z.infer<typeof bulkEditResultSchema>;

export const importCsvInputSchema = z.object({ csv: z.string().min(1).max(500_000) });
export const importResultSchema = z.object({
  created: z.number().int(),
  failed: z.number().int(),
  /** Imported without inbox because the plan's inbox limit was reached. */
  inboxDisabled: z.number().int(),
});
export type ImportResult = z.infer<typeof importResultSchema>;

/** The columns the bulk-import template carries, in order. */
export const CSV_TEMPLATE_COLUMNS = [
  "sender_name",
  "email",
  "smtp_host",
  "smtp_port",
  "smtp_username",
  "smtp_password",
  "imap_host",
  "imap_port",
  "messages_per_day",
  "delay_seconds",
] as const;

export const emptyInputSchema = z.object({});
export const senderIdInputSchema = z.object({ senderId: z.string() });
export const senderIdsInputSchema = z.object({ senderIds: z.array(z.string()).min(1) });
export const moveSendersInputSchema = z.object({
  senderIds: z.array(z.string()).min(1),
  projectId: z.string(),
});
export const countSchema = z.object({ changed: z.number().int().nonnegative() });
export type Count = z.infer<typeof countSchema>;
